import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Chip,
  Link,
  Dialog,
  AppBar,
  Toolbar,
  Slide,
  Button,
  Modal,
  Divider,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Portal,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import CloseIcon from "@mui/icons-material/Close";
import SimCardIcon from "@mui/icons-material/SimCard";
import SouthIcon from "@mui/icons-material/South";
import NorthIcon from "@mui/icons-material/North";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

import { navigateToOrganization } from "../../../../utils/navigateToOrganization";
import {
  parseDateFromInput,
  formatDateWithMask,
} from "../../../../utils/dateUtils";
import { useCertificateStatus } from "../../../../hooks/useCertificateStatus";
import { A1ExpiricyProps } from "../../../../types/certificate";
import { organizationProductsLabels } from "../../../../utils/labels";

const Transition = React.forwardRef((props: any, ref) => {
  return <Slide direction="up" {...props} ref={ref} />;
});
Transition.displayName = "Transition";

const A1Expiricy = ({
  data,
  profileFilter = null,
  certificateCount,
  noCertificateCount,
  validCertificateCount,
  autoFilterProfile = null,
  autoFilterStatus = null,
  showAllCertificatesButton = false,
}: A1ExpiricyProps) => {
  const navigate = useNavigate();
  const { getCertificateStatus, getProfileName } = useCertificateStatus();

  const [state, setState] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sortBy, setSortBy] = useState("reference");
  const [order, setOrder] = useState("");
  const [pendingFilterStatus, setPendingFilterStatus] = useState<
    string[] | null
  >(null);

  const initialFilters = useMemo(
    () => ({
      reference: "",
      name: "",
      dateFrom: "",
      dateTo: "",
      profiles: autoFilterProfile ? [autoFilterProfile] : ([] as string[]),
      statuses: autoFilterStatus
        ? Array.isArray(autoFilterStatus)
          ? autoFilterStatus
          : [autoFilterStatus]
        : ([] as string[]),
      products: [] as string[],
    }),
    [autoFilterProfile, autoFilterStatus],
  );

  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    if (!state) {
      setFilters(initialFilters);
      setPendingFilterStatus(null);
    }
  }, [state, initialFilters]);

  useEffect(() => {
    if (
      state &&
      pendingFilterStatus &&
      (certificateCount !== undefined ||
        noCertificateCount !== undefined ||
        validCertificateCount !== undefined)
    ) {
      setFilters((prev) => ({
        ...prev,
        statuses: pendingFilterStatus,
        profiles: profileFilter ? [profileFilter] : [],
        products: [],
      }));
      setPendingFilterStatus(null);
    }
  }, [
    state,
    pendingFilterStatus,
    profileFilter,
    certificateCount,
    noCertificateCount,
    validCertificateCount,
  ]);

  const handleFilterChange = useCallback((field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleProfileChange = useCallback(
    (profile: string, checked: boolean) => {
      setFilters((prev) => ({
        ...prev,
        profiles: checked
          ? [...prev.profiles, profile]
          : prev.profiles.filter((p) => p !== profile),
      }));
    },
    [],
  );

  const handleStatusChange = useCallback((status: string, checked: boolean) => {
    setFilters((prev) => ({
      ...prev,
      statuses: checked
        ? [...prev.statuses, status]
        : prev.statuses.filter((s) => s !== status),
    }));
  }, []);

  const handleProductChange = useCallback(
    (product: string, checked: boolean) => {
      setFilters((prev) => ({
        ...prev,
        products: checked
          ? [...prev.products, product]
          : prev.products.filter((p) => p !== product),
      }));
    },
    [],
  );

  const handleDateChange = useCallback((field: string, value: string) => {
    const formattedValue = formatDateWithMask(value);
    if (formattedValue.length <= 10) {
      setFilters((prev) => ({ ...prev, [field]: formattedValue }));
    }
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      reference: "",
      name: "",
      dateFrom: "",
      dateTo: "",
      profiles: [],
      statuses: [],
      products: [],
    });
    setSortBy("reference");
    setOrder("");
  }, []);

  const handleSort = useCallback((field: string) => {
    setSortBy(field);
  }, []);

  const handleOrder = useCallback((newOrder: string) => {
    setOrder(newOrder);
  }, []);

  const availableProfiles = useMemo(() => {
    if (!data?.length) return [];

    const profiles = new Set<string>();
    data.forEach((org) => {
      const profileName = getProfileName(org);
      if (profileName && profileName !== "-") {
        profiles.add(profileName);
      }
    });

    return Array.from(profiles).sort((a, b) => a.localeCompare(b));
  }, [data, getProfileName]);

  const columns: GridColDef[] = [
    {
      field: "reference",
      headerName: "Cód.",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <Link
            component="button"
            onClick={() => {
              navigateToOrganization(params.row.reference, navigate);
            }}
          >
            {params.row.reference}
          </Link>
        );
      },
    },
    {
      field: "name",
      headerName: "Nome",
      flex: 2,
      minWidth: 200,
      sortable: false,
      renderCell: (params: any) => {
        return params.row.name || "-";
      },
    },
    {
      field: "certificateStatus",
      headerName: "Status do Certificado",
      flex: 1.5,
      minWidth: 180,
      sortable: false,
      renderCell: (params: any) => {
        const statusInfo = getCertificateStatus(params.row);
        return (
          <Chip
            label={statusInfo.status}
            color={statusInfo.color as any}
            size="small"
            variant="filled"
          />
        );
      },
    },
    {
      field: "profile",
      headerName: "Carteira",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params: any) => {
        return getProfileName(params.row);
      },
    },
    {
      field: "expiryDate",
      headerName: "Data de Vencimento",
      flex: 1.5,
      minWidth: 180,
      sortable: false,
      renderCell: (params: any) => {
        if (!params.row.digital_certificate?.expiricy) {
          return "-";
        }
        return new Date(
          params.row.digital_certificate.expiricy,
        ).toLocaleDateString("pt-BR");
      },
    },
  ];

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let filtered = [...data];

    if (filters.reference) {
      filtered = filtered.filter((org) => {
        const reference = String(org.reference || "");
        return reference
          .toLowerCase()
          .includes(filters.reference.toLowerCase());
      });
    }

    if (filters.name) {
      filtered = filtered.filter((org) =>
        org.name?.toLowerCase().includes(filters.name.toLowerCase()),
      );
    }

    if (filters.profiles.length > 0) {
      filtered = filtered.filter((org) => {
        const profileName = getProfileName(org);
        return filters.profiles.includes(profileName);
      });
    }

    if (filters.products.length > 0) {
      filtered = filtered.filter((org) =>
        filters.products.some(
          (product) => org.products?.[product]?.active === true,
        ),
      );
    }

    if (filters.statuses.length > 0) {
      filtered = filtered.filter((org) => {
        const statusInfo = getCertificateStatus(org);
        return filters.statuses.includes(statusInfo.status);
      });
    }

    if (filters.dateFrom) {
      const fromDate = parseDateFromInput(filters.dateFrom);
      if (fromDate) {
        filtered = filtered.filter((org) => {
          if (!org.digital_certificate?.expiricy) return false;
          const expiryDate = new Date(org.digital_certificate.expiricy);
          return expiryDate >= fromDate;
        });
      }
    }

    if (filters.dateTo) {
      const toDate = parseDateFromInput(filters.dateTo);
      if (toDate) {
        filtered = filtered.filter((org) => {
          if (!org.digital_certificate?.expiricy) return false;
          const expiryDate = new Date(org.digital_certificate.expiricy);
          return expiryDate <= toDate;
        });
      }
    }

    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "reference": {
          const refA = String(a.reference || "");
          const refB = String(b.reference || "");
          const numA = parseInt(refA.replace(/\D/g, "")) || 0;
          const numB = parseInt(refB.replace(/\D/g, "")) || 0;
          comparison = numA - numB;
          break;
        }
        case "name": {
          comparison = String(a.name || "").localeCompare(String(b.name || ""));
          break;
        }
        case "profile": {
          comparison = getProfileName(a).localeCompare(getProfileName(b));
          break;
        }
        case "expiryDate": {
          const dateA = a.digital_certificate?.expiricy
            ? new Date(a.digital_certificate.expiricy)
            : new Date(0);
          const dateB = b.digital_certificate?.expiricy
            ? new Date(b.digital_certificate.expiricy)
            : new Date(0);
          comparison = dateA.getTime() - dateB.getTime();
          break;
        }
        default:
          comparison = 0;
      }

      return order === "desc" ? -comparison : comparison;
    });

    return filtered;
  }, [data, filters, sortBy, order, getCertificateStatus, getProfileName]);

  const exportCertificatesExcel = useCallback(async () => {
    try {
      const dataToExport = filteredData.map((org) => ({
        reference: org.reference,
        name: org.name,
        profile: getProfileName(org),
        certificateStatus: getCertificateStatus(org).status,
        expiryDate: org.digital_certificate?.expiricy
          ? new Date(org.digital_certificate.expiricy).toLocaleDateString(
              "pt-BR",
            )
          : "-",
        password: org.digital_certificate?.password || "-",
      }));

      const [{ default: XLSX }, { default: FileSaver }] = await Promise.all([
        import("sheetjs-style"),
        import("file-saver"),
      ]);

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const file = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      FileSaver.saveAs(file, "certificados-digitais.xlsx");
    } catch (error) {
      console.error("Erro ao exportar certificados:", error);
    }
  }, [filteredData, getProfileName, getCertificateStatus]);

  const handleCloseDialog = () => {
    setState(false);
    setDrawerOpen(false);
    resetFilters();
  };

  const activeFiltersCount = useMemo(() => {
    const textFilters = [
      filters.reference,
      filters.name,
      filters.dateFrom,
      filters.dateTo,
    ].filter((text) => text.trim()).length;

    const arrayFilters =
      filters.profiles.length +
      filters.statuses.length +
      filters.products.length;

    return textFilters + arrayFilters;
  }, [filters]);

  const handleOpenFilters = () => {
    setDrawerOpen(true);
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        sx={{
          width: "100%",
          flexWrap: "nowrap",
          "& > .MuiButton-root": {
            flex: "1 1 0",
            minWidth: 0,
            px: 0.75,
            "& .MuiButton-startIcon": {
              mr: 0.5,
              "& > *:nth-of-type(1)": {
                fontSize: 16,
              },
            },
          },
        }}
      >
        {showAllCertificatesButton && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SimCardIcon />}
            onClick={() => {
              setState(true);
            }}
            sx={{
              color: "text.primary",
              borderColor: "divider",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "transparent",
              },
            }}
          >
            Ver todos
          </Button>
        )}
        {validCertificateCount !== undefined && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SimCardIcon />}
            onClick={() => {
              setPendingFilterStatus(["Válido"]);
              setState(true);
            }}
            sx={{
              color:
                validCertificateCount > 0 ? "success.main" : "text.primary",
              borderColor: "divider",
              "&:hover": {
                borderColor:
                  validCertificateCount > 0 ? "success.main" : "primary.main",
                backgroundColor: "transparent",
              },
            }}
          >
            {validCertificateCount}
          </Button>
        )}
        {noCertificateCount !== undefined && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SimCardIcon />}
            onClick={() => {
              setPendingFilterStatus(["Não possui"]);
              setState(true);
            }}
            sx={{
              color:
                noCertificateCount && noCertificateCount > 0
                  ? "error.main"
                  : "text.primary",
              borderColor: "divider",
              "&:hover": {
                borderColor:
                  noCertificateCount && noCertificateCount > 0
                    ? "error.main"
                    : "primary.main",
                backgroundColor: "transparent",
              },
            }}
          >
            {noCertificateCount}
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<SimCardIcon />}
          onClick={() => {
            setPendingFilterStatus(["Vencido", "À vencer"]);
            setState(true);
          }}
          sx={{
            color:
              certificateCount && certificateCount > 0
                ? "warning.main"
                : "text.primary",
            borderColor: "divider",
            "&:hover": {
              borderColor:
                certificateCount && certificateCount > 0
                  ? "warning.main"
                  : "primary.main",
              backgroundColor: "transparent",
            },
          }}
        >
          {certificateCount !== undefined ? certificateCount : "Certificados"}
        </Button>
      </Stack>
      <Dialog
        open={state}
        onClose={handleCloseDialog}
        fullScreen
        TransitionComponent={Transition}
      >
        <AppBar position="fixed">
          <Toolbar>
            <Typography sx={{ flex: 1 }} variant="h6" component="div">
              Certificados Digitais
            </Typography>
            <Box sx={{ position: "relative", mr: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenFilters}
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[700]
                      : theme.palette.grey[100],
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[100]
                      : theme.palette.grey[800],
                  "&:hover": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[600]
                        : theme.palette.grey[200],
                  },
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  Filtros
                </Typography>
              </Button>
              {activeFiltersCount > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -8,
                    left: -8,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "primary.main" : "black",
                    color: "white",
                    borderRadius: "50%",
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                  }}
                >
                  {activeFiltersCount}
                </Box>
              )}
            </Box>
            <IconButton
              color="inherit"
              onClick={exportCertificatesExcel}
              sx={{ mr: 2 }}
            >
              <DownloadOutlinedIcon />
            </IconButton>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseDialog}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Toolbar />
        <Box sx={{ p: 3, height: "calc(100vh - 64px)" }}>
          <DataGrid
            rows={filteredData}
            columns={columns}
            getRowId={(row) => row._id}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 25,
                },
              },
            }}
            pageSizeOptions={[25, 50, 100]}
            sx={{
              height: "100%",
            }}
            disableColumnMenu
            disableRowSelectionOnClick
          />
        </Box>
      </Dialog>

      <Portal>
        <Modal
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Box>
            <Slide direction="left" in={drawerOpen} mountOnEnter unmountOnExit>
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  height: "100vh",
                  width: 300,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "#424242" : "#f5f5f5",
                  boxShadow: 24,
                  p: 3,
                  pt: 2,
                }}
              >
                <Stack height="100%" spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ flex: 1 }}
                      alignItems="center"
                    >
                      <Typography
                        variant="body1"
                        sx={{ width: 170, wordWrap: "break-word" }}
                      >
                        Opções
                      </Typography>
                    </Stack>
                    <IconButton onClick={() => setDrawerOpen(false)}>
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                  <Divider />

                  <Box
                    sx={{
                      flex: 1,
                      overflow: "auto",
                    }}
                  >
                    <Stack spacing={2} sx={{ pr: 2 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" fontWeight="bold">
                          Ordenação
                        </Typography>
                        <ToggleButtonGroup
                          color="primary"
                          value={order}
                          exclusive
                          onChange={(_, newOrder) =>
                            handleOrder(newOrder || "")
                          }
                          aria-label="text alignment"
                          size="small"
                        >
                          <ToggleButton value="" aria-label="ascending order">
                            <SouthIcon fontSize="small" />
                          </ToggleButton>
                          <ToggleButton
                            value="desc"
                            aria-label="descending order"
                          >
                            <NorthIcon fontSize="small" />
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>
                      <ToggleButtonGroup
                        color="primary"
                        value={sortBy}
                        exclusive
                        onChange={(_, newValue) =>
                          handleSort(newValue || "reference")
                        }
                        aria-label="Ordernar Por"
                        size="small"
                        fullWidth
                      >
                        <ToggleButton value="reference">Código</ToggleButton>
                        <ToggleButton value="name">Nome</ToggleButton>
                        <ToggleButton value="expiryDate">
                          Vencimento
                        </ToggleButton>
                      </ToggleButtonGroup>

                      <Divider />

                      <Typography variant="body2" fontWeight="bold">
                        Filtros
                      </Typography>

                      <TextField
                        label="Código"
                        variant="outlined"
                        size="small"
                        value={filters.reference}
                        onChange={(e) =>
                          handleFilterChange("reference", e.target.value)
                        }
                      />

                      <TextField
                        label="Nome"
                        variant="outlined"
                        size="small"
                        value={filters.name}
                        onChange={(e) =>
                          handleFilterChange("name", e.target.value)
                        }
                      />

                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ mt: 1 }}
                      >
                        Data de vencimento
                      </Typography>
                      <TextField
                        label="A partir de"
                        variant="outlined"
                        size="small"
                        placeholder="dd/mm/yyyy"
                        value={filters.dateFrom}
                        onChange={(e) =>
                          handleDateChange("dateFrom", e.target.value)
                        }
                        inputProps={{
                          maxLength: 10,
                        }}
                      />

                      <TextField
                        label="Até"
                        variant="outlined"
                        size="small"
                        placeholder="dd/mm/yyyy"
                        value={filters.dateTo}
                        onChange={(e) =>
                          handleDateChange("dateTo", e.target.value)
                        }
                        inputProps={{
                          maxLength: 10,
                        }}
                      />

                      <Typography variant="body2" fontWeight="bold">
                        Status
                      </Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={filters.statuses.includes("Válido")}
                              onChange={(e) =>
                                handleStatusChange("Válido", e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label="Válido"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={filters.statuses.includes("Vencido")}
                              onChange={(e) =>
                                handleStatusChange("Vencido", e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label="Vencido"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={filters.statuses.includes("Não possui")}
                              onChange={(e) =>
                                handleStatusChange(
                                  "Não possui",
                                  e.target.checked,
                                )
                              }
                              size="small"
                            />
                          }
                          label="Não possui"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={filters.statuses.includes("À vencer")}
                              onChange={(e) =>
                                handleStatusChange("À vencer", e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label="À vencer"
                        />
                      </FormGroup>

                      <Typography variant="body2" fontWeight="bold">
                        Carteira
                      </Typography>
                      <FormGroup>
                        {availableProfiles.map((profile) => (
                          <FormControlLabel
                            key={profile}
                            control={
                              <Checkbox
                                checked={filters.profiles.includes(profile)}
                                onChange={(e) =>
                                  handleProfileChange(profile, e.target.checked)
                                }
                                size="small"
                              />
                            }
                            label={profile}
                          />
                        ))}
                      </FormGroup>

                      <Typography variant="body2" fontWeight="bold">
                        Serviço
                      </Typography>
                      <FormGroup>
                        {organizationProductsLabels.map(({ key, label }) => (
                          <FormControlLabel
                            key={key}
                            control={
                              <Checkbox
                                checked={filters.products.includes(key)}
                                onChange={(e) =>
                                  handleProductChange(key, e.target.checked)
                                }
                                size="small"
                              />
                            }
                            label={label}
                          />
                        ))}
                      </FormGroup>

                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={resetFilters}
                        fullWidth
                      >
                        Limpar Filtros
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Slide>
          </Box>
        </Modal>
      </Portal>
    </>
  );
};

export default A1Expiricy;
